import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { Dropdown } from '../../components/Dropdown';
import { EmptyMessage } from '../../components/EmptyMessage';
import { InlineFeedback } from '../../components/InlineFeedback';
import { Section, Title, TitleRow } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { ViewToggle } from '../../components/ViewToggle';
import { SpaClassCard } from '../../composites/SpaClassCard';
import { SpaClassCompactCard } from '../../composites/SpaClassCompactCard';
import {
  CategoryDropdownContainer,
  CompactGrid,
  FilterContainer,
  GroupHeader,
  GroupHeaderText,
  GroupLabel,
  GroupRule,
  GroupsContainer,
  GroupSubLabel,
  ToggleContainer,
} from './SpaClassListTemplate.styles';
import type { SpaClassListItem, SpaClassListTemplateProps } from './SpaClassListTemplate.types';

export function SpaClassListTemplate({
  items,
  onSelectItem,
  isLoading,
  view = 'grid',
  onViewChange,
  groups,
  categoryOptions,
  selectedCategory,
  onCategoryChange,
  emptyState,
  noResultsFeedback,
  titleLabel = 'Activities',
  categoryLabel = 'Type',
  soldOutLabel = 'Sold out',
  noResultsLabel = 'No classes available.',
  noImageLabel = 'No image',
  lowAvailabilityThreshold,
  formatSpotsLeft,
  gridViewLabel,
  compactViewLabel,
}: SpaClassListTemplateProps) {
  const renderGridCard = (item: SpaClassListItem) => (
    <SpaClassCard
      key={item.id}
      id={item.id}
      name={item.name}
      time={item.time}
      imageUrl={item.imageUrl}
      hideImage={item.hideImage}
      price={item.price}
      discount={item.discount}
      duration={item.duration}
      instructor={item.instructor}
      spotsLeft={item.spotsLeft}
      unavailable={item.unavailable}
      promotionName={item.promotionName}
      lowAvailabilityThreshold={lowAvailabilityThreshold}
      soldOutLabel={soldOutLabel}
      noImageLabel={noImageLabel}
      formatSpotsLeft={formatSpotsLeft}
      onSelect={onSelectItem}
    />
  );

  const renderCompactCard = (item: SpaClassListItem) => (
    <SpaClassCompactCard
      key={item.id}
      id={item.id}
      name={item.name}
      time={item.time}
      price={item.price}
      discount={item.discount}
      duration={item.duration}
      instructor={item.instructor}
      spotsLeft={item.spotsLeft}
      unavailable={item.unavailable}
      promotionName={item.promotionName}
      lowAvailabilityThreshold={lowAvailabilityThreshold}
      soldOutLabel={soldOutLabel}
      formatSpotsLeft={formatSpotsLeft}
      onSelect={onSelectItem}
    />
  );

  const filters = (
    <FilterContainer>
      {categoryOptions && categoryOptions.length > 0 && onCategoryChange ? (
        <CategoryDropdownContainer>
          <Dropdown
            options={categoryOptions}
            value={selectedCategory}
            placeholder={categoryLabel}
            onChange={onCategoryChange}
            variant="pill"
            aria-label={categoryLabel}
          />
        </CategoryDropdownContainer>
      ) : (
        <span />
      )}
      {onViewChange && (
        <ToggleContainer>
          <ViewToggle
            value={view}
            onChange={onViewChange}
            gridLabel={gridViewLabel}
            compactLabel={compactViewLabel}
          />
        </ToggleContainer>
      )}
    </FilterContainer>
  );

  if (isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filters}
        </TitleRow>
        <CardGrid $columns={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} variant={view === 'compact' ? 'list' : 'grid'} />
          ))}
        </CardGrid>
      </Section>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filters}
        </TitleRow>
        {noResultsFeedback ? (
          <InlineFeedback {...noResultsFeedback} />
        ) : (
          emptyState ?? <EmptyMessage>{noResultsLabel}</EmptyMessage>
        )}
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
        {filters}
      </TitleRow>

      <ContentArea>
        {view === 'compact' && groups && groups.length > 0 ? (
          <GroupsContainer>
            {groups.map((group) => (
              <div key={group.id}>
                <GroupHeader>
                  <GroupHeaderText>
                    <GroupLabel>{group.label}</GroupLabel>
                    {group.subLabel && <GroupSubLabel>{group.subLabel}</GroupSubLabel>}
                  </GroupHeaderText>
                  <GroupRule aria-hidden="true" />
                </GroupHeader>
                <CompactGrid>{group.items.map(renderCompactCard)}</CompactGrid>
              </div>
            ))}
          </GroupsContainer>
        ) : view === 'compact' ? (
          <CompactGrid>{items.map(renderCompactCard)}</CompactGrid>
        ) : (
          <CardGrid $columns={3}>{items.map(renderGridCard)}</CardGrid>
        )}
      </ContentArea>
    </Section>
  );
}
